<template>
  <div v-if="initialized && !canApplyOrHasApplications">
    <div class="notch-wrapper">
      <div class="notch" />
    </div>
    <CantUseFeature />
    <AppFooter />
    <BlockBack />
  </div>
  <div v-else>
    <Header ref="header">
      仕事管理
      <template v-slot:right>
        <a :href="externalLink.attendance">
          <FeatherIcon icon="help-circle" size="1x" class="text-gray-05 mr-2" />
        </a>
        <AttendanceButton
          v-if="doingApplication || mostRecentAppliedApplication"
          key="enableButton"
          :isAttendance="!!doingApplication"
          @click-attendance="checkAttendanceTime"
        />
        <AttendanceButton v-else key="disableButton" isDisabled />
      </template>
    </Header>
    <ApplicationsNav :links="applicationNavItems" />

    <template v-if="initializedApplications">
      <ApplicationsAppliedList
        v-if="isAppliedPage"
        @clickOrderCard="goToApplicationsDetail"
      />

      <ApplicationsAttendanceList
        v-else-if="isAttendancePage"
        @clickOrderCard="goToApplicationsDetail"
      />

      <ApplicationsNotEvaluatedList
        v-else-if="isNotEvaluatedPage"
        @clickOrderCard="goToApplicationsDetail"
      />
    </template>

    <ApplicationsClosedList
      v-if="isEvaluatedPage || isCanceledPage"
      @clickOrderCard="goToApplicationsDetail"
    />

    <AppFooter />
    <BlockBack />

    <!-- 出勤可能時刻未達の場合に表示するモーダル -->
    <NotStartWorkModal
      v-if="mostRecentAppliedApplication"
      :visible="isShownNotStartWorkModal"
      :application="mostRecentAppliedApplication"
      @close="isShownNotStartWorkModal = false"
    />

    <!-- 未出勤モーダル -->
    <BModal
      v-model="isShownAttendNeededModal"
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <div class="px-2 pb-2" data-testid="attend-needed-modal-wrapper">
        <p>未出勤のお仕事があります！早急に出勤処理を行ってください</p>
        <AppButton
          @click="goToAttendancePage(false)"
          :block="true"
          class="d-block mb-3 mt-1"
        >
          出勤する
        </AppButton>
        <p class="font-weight-bold">緊急連絡先</p>
        <ApplicationOfficeDetail
          v-if="firstNotAttendedAndCorrectedApplication"
          :office="firstNotAttendedAndCorrectedApplication.order.office"
        />
        <p class="font-weight-bold mt-4">キャンセルの場合</p>
        <p>運営事務局へお問い合わせください</p>
        <AppButtonOutline
          :href="externalLink.contact"
          size="small"
          block
          class="mb-3 mt-1"
        >
          お問い合わせフォーム
        </AppButtonOutline>
        <p>
          お仕事終了日から1週間ご連絡がない場合、一時的にアカウントが利用停止となります
        </p>
      </div>
    </BModal>

    <!-- 退勤時に表示する遅刻・早退確認モーダル -->
    <ConfirmAttendanceCorrectionRequiredModal
      v-if="doingApplication"
      :visible="isShownConfirmAttendanceCorrectionRequiredModal"
      :application="doingApplication"
      @close="isShownConfirmAttendanceCorrectionRequiredModal = false"
      @finishWork="finishWork"
    />

    <!-- 退勤時に表示する残業有無確認モーダル -->
    <ConfirmDiscrepancyTimeModal
      v-if="doingApplication"
      :visible="isShownConfirmDiscrepancyTimeModal"
      :application="doingApplication"
      @close="isShownConfirmDiscrepancyTimeModal = false"
      @finishWork="finishWork"
    />

    <!-- 未退勤モーダル -->
    <LeavingWorkNeededModal
      v-if="doingApplication"
      :visible="isShownLeavingWorkNeededModal"
      :application="doingApplication"
      @finishWork="finishWork"
    />

    <!-- 未退勤かつ勤怠変更申請が必ず必要な場合に表示されるモーダル -->
    <LeavingWorkNeededWithAttendanceCorrectionModal
      v-if="doingApplication"
      :visible="isShownLeavingWorkNeededWithAttendanceCorrectionModal"
      :application="doingApplication"
      @finishWork="finishWork"
    />

    <!-- 未出勤のまま退勤時間を超過した際に表示されるモーダル -->
    <NotAttendedModal
      v-if="firstNotAttendedAndCorrectedApplication"
      :visible="isShownNotAttendedModal"
      :application="firstNotAttendedAndCorrectedApplication"
    />
  </div>
</template>

<script lang="ts">
import { accountService } from "@/app/services/AccountService";
import { applicationService } from "@/app/services/ApplicationService";
import { salaryService } from "@/app/services/SalaryService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import AttendanceButton from "@/components/atoms/button/AttendanceButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import CantUseFeature from "@/components/organisms/accounts/CantUseFeature.vue";
import ApplicationsAppliedList from "@/components/organisms/applications/ApplicationsAppliedList.vue";
import ApplicationsAttendanceList from "@/components/organisms/applications/ApplicationsAttendanceList.vue";
import ApplicationsClosedList from "@/components/organisms/applications/ApplicationsClosedList.vue";
import ApplicationsNav from "@/components/organisms/applications/ApplicationsNav.vue";
import ApplicationsNotEvaluatedList from "@/components/organisms/applications/ApplicationsNotEvaluatedList.vue";
import ApplicationOfficeDetail from "@/components/organisms/applications/ApplicationsOfficeDetail.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import Header from "@/components/organisms/common/Header.vue";
import applicationTab from "@/models/applicationTab";
import verifiedStatus from "@/models/verifiedStatus";
import { applicationStore } from "@/store/application";
import { authStore } from "@/store/auth";
import { ApplicationsListNavItem } from "@/types/application/ApplicationsListNavItem";
import {
  addMinutes,
  convertToJST,
  now,
  parseISO,
  startOfDay,
  startOfToday,
  subMinutes
} from "@/utils/date";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import ConfirmAttendanceCorrectionRequiredModal from "@/views/applications/organisms/ConfirmAttendanceCorrectionRequiredModal.vue";
import ConfirmDiscrepancyTimeModal from "@/views/applications/organisms/ConfirmDiscrepancyTimeModal.vue";
import LeavingWorkNeededModal from "@/views/applications/organisms/LeavingWorkNeededModal.vue";
import LeavingWorkNeededWithAttendanceCorrectionModal from "@/views/applications/organisms/LeavingWorkNeededWithAttendanceCorrectionModal.vue";
import NotAttendedModal from "@/views/applications/organisms/NotAttendedModal.vue";
import NotStartWorkModal from "@/views/applications/organisms/NotStartWorkModal.vue";
import { Component, Vue, Watch } from "vue-facing-decorator";
import { RouteLocationNormalizedLoaded } from "vue-router";

@Component({
  components: {
    AppButton,
    AppButtonOutline,
    FeatherIcon,
    ApplicationsAppliedList,
    ApplicationsAttendanceList,
    ApplicationsNotEvaluatedList,
    ApplicationsClosedList,
    AppFooter,
    ApplicationsNav,
    AttendanceButton,
    BlockBack,
    Header,
    ApplicationOfficeDetail,
    CantUseFeature,
    ConfirmAttendanceCorrectionRequiredModal,
    ConfirmDiscrepancyTimeModal,
    LeavingWorkNeededModal,
    NotAttendedModal,
    NotStartWorkModal,
    LeavingWorkNeededWithAttendanceCorrectionModal
  }
})
export default class ApplicationsList extends Vue {
  isShownConfirmAttendanceCorrectionRequiredModal = false;
  isShownConfirmDiscrepancyTimeModal = false;
  isShownNotStartWorkModal = false;
  hasVerifiedQualification = false;
  hasBankAccount = false;
  initialized = false;
  initializedApplications = false;

  externalLink = {
    attendance: process.env.VUE_APP_URL_ATTENDANCE,
    contact: process.env.VUE_APP_URL_CONTACT
  };

  get canApplyOrHasApplications() {
    if (
      (authStore.user && authStore.user.workCount > 0) ||
      applicationStore.applications.length > 0
    ) {
      return true;
    }
    return (
      authStore.hasVerifiedAccountInfo &&
      authStore.hasRegisteredBusinessInfo &&
      this.hasVerifiedQualification &&
      this.hasBankAccount
    );
  }
  get isAppliedPage() {
    return this.$route.query.tab === applicationTab.APPLIED;
  }
  get isNotEvaluatedPage() {
    return this.$route.query.tab === applicationTab.NOT_EVALUATED;
  }
  get isEvaluatedPage() {
    return this.$route.query.tab === applicationTab.EVALUATED;
  }
  get isCanceledPage() {
    return this.$route.query.tab === applicationTab.CANCELED;
  }
  get isAttendancePage() {
    return this.$route.query.tab === applicationTab.ATTENDANCE;
  }
  get doingApplication() {
    return applicationStore.doingApplication;
  }
  get mostRecentAppliedApplication() {
    return applicationStore.mostRecentAppliedApplication;
  }
  get applicationNavItems(): ApplicationsListNavItem[] {
    return [
      {
        title: "予定",
        tab: applicationTab.APPLIED,
        count: 0
      },
      {
        title: "勤怠",
        tab: applicationTab.ATTENDANCE,
        count: applicationStore.attendanceApplicationCount
      },
      {
        title: "未評価",
        tab: applicationTab.NOT_EVALUATED,
        count: applicationStore.notEvaluatedApplicationCount
      },
      {
        title: "完了",
        tab: applicationTab.EVALUATED,
        count: 0
      },
      {
        title: "キャンセル",
        tab: applicationTab.CANCELED,
        count: 0
      }
    ];
  }

  get canAttend() {
    return applicationService.canAttend;
  }

  get isAttendanceCorrectionRequired() {
    if (!this.doingApplication) {
      return false;
    }
    // すでに遅刻している場合は要勤怠変更
    if (this.doingApplication?.attendanceCorrection) {
      return true;
    }
    // 予定の終了時間の5分前より早い場合は早退
    const endTime = parseISO(this.doingApplication.appliedFinishWorkAt);
    const nowTime = now();
    if (nowTime < subMinutes(endTime, 5)) {
      return true;
    }
    // 現在の日付が予定の終了日付より後の場合は要勤怠変更
    const endDate = startOfDay(endTime);
    const today = startOfToday();
    return today > endDate;
  }

  // 1件目の未出勤かつ勤怠変更申請なし案件
  get firstNotAttendedAndCorrectedApplication() {
    const applications = applicationStore.notAttendedAndCorrectedApplications;
    if (applications.length === 0) return null;
    return applications[0];
  }

  // 1件目の未退勤案件
  get firstNotLeavingWorkApplication() {
    const applications = applicationStore.notLeavingWorkApplications;
    if (applications.length === 0) return null;
    return applications[0];
  }

  // 出勤を促すモーダルダイアログを開くかどうか
  get isShownAttendNeededModal() {
    if (this.firstNotAttendedAndCorrectedApplication === null) return false;
    return (
      now() <
      parseISO(this.firstNotAttendedAndCorrectedApplication.appliedFinishWorkAt)
    );
  }

  // 退勤を促すモーダルダイアログを開くかどうか
  get isShownLeavingWorkNeededModal() {
    if (this.firstNotLeavingWorkApplication === null) return false;
    // 勤怠変更申請が必ず必要な場合は表示しない
    if (this.isShownLeavingWorkNeededWithAttendanceCorrectionModal) {
      return false;
    }
    return (
      now() >
      addMinutes(
        parseISO(this.firstNotLeavingWorkApplication.appliedFinishWorkAt),
        15
      )
    );
  }

  // 未退勤かつ勤怠変更申請が必ず必要な場合に表示されるモーダル
  get isShownLeavingWorkNeededWithAttendanceCorrectionModal() {
    if (this.firstNotLeavingWorkApplication === null) {
      return false;
    }

    // 勤怠変更申請が既にある場合は遅刻をしているため要勤怠変更
    if (this.firstNotLeavingWorkApplication.attendanceCorrection !== null) {
      return true;
    }

    return (
      startOfToday() >
      startOfDay(
        convertToJST(
          parseISO(this.firstNotLeavingWorkApplication.appliedFinishWorkAt)
        )
      )
    );
  }

  // 未出退勤による勤怠変更申請を促すモーダルダイアログを開くかどうか
  get isShownNotAttendedModal() {
    if (this.firstNotAttendedAndCorrectedApplication === null) return false;
    return !(
      now() <
      parseISO(this.firstNotAttendedAndCorrectedApplication.appliedFinishWorkAt)
    );
  }

  @Watch("$route", { immediate: true })
  async onChangeRoute(
    newVal: RouteLocationNormalizedLoaded,
    oldVal?: RouteLocationNormalizedLoaded
  ) {
    // 自ページへの遷移以外は何もしない
    if (newVal.name !== "ApplicationsList") {
      return;
    }
    // 同じパスへの遷移は何もしない
    if (newVal.fullPath === oldVal?.fullPath) {
      return;
    }
    // 遷移先のクエリパラメータに tab が存在しない場合はパラメータを付与
    if (!newVal.query.tab) {
      this.$router.replace({
        query: { tab: "applied" }
      });
      return;
    }
    // 完了・キャンセルタブの場合は ApplicationsClosedList コンポーネント内でデータを読み込むため、応募データの取得は不要
    if (
      [applicationTab.EVALUATED, applicationTab.CANCELED].includes(
        (newVal.query.tab as string) || ""
      )
    ) {
      return;
    }
    try {
      this.initializedApplications = false;
      this.$loading.show();
      await applicationStore.fetchApplications();
      this.initializedApplications = true;
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async finishWork(withAttendanceCorrection: boolean) {
    await this.goToAttendancePage(true, withAttendanceCorrection);
  }

  async goToAttendancePage(isAttendance: boolean, isOvertime = false) {
    this.isShownConfirmDiscrepancyTimeModal = false;
    this.isShownConfirmAttendanceCorrectionRequiredModal = false;
    await this.$router.push({
      name: "QrScannerWeb",
      query: {
        workStatus: isAttendance ? "finish" : "start",
        id: isAttendance
          ? this.doingApplication?.id.toString()
          : this.mostRecentAppliedApplication?.id.toString(),
        isOvertime: isOvertime.toString()
      }
    });
  }

  async goToApplicationsDetail(applicationId: string) {
    await this.$router.push({
      name: "ApplicationsDetail",
      params: {
        applicationId
      }
    });
  }

  async checkAttendanceTime() {
    // 出勤（＝勤務中案件なし）
    if (!this.doingApplication) {
      // 開始時刻の1時間以上前は出勤処理は行わない
      if (!this.canAttend) {
        this.isShownNotStartWorkModal = true;
        return;
      }

      // 通常の出退勤処理
      await this.goToAttendancePage(false);
      return;
    }

    // 以降は退勤
    // 要勤怠変更確認モーダル表示
    if (this.isAttendanceCorrectionRequired) {
      this.isShownConfirmAttendanceCorrectionRequiredModal = true;
      return;
    }

    // 残業有無確認モーダル表示
    this.isShownConfirmDiscrepancyTimeModal = true;
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchHasBankAccount(),
        this.fetchHasVerifiedQualification()
      ]);
      this.initialized = true;
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchHasBankAccount() {
    this.hasBankAccount = await salaryService.fetchHasBankAccount();
  }

  async fetchHasVerifiedQualification() {
    const { results } = await accountService.fetchQualifications();
    this.hasVerifiedQualification = results.some(
      q => q.status === verifiedStatus.VERIFIED
    );
  }
}
</script>

<style lang="scss" scoped>
.notch-wrapper {
  padding-top: env(safe-area-inset-top, 0);

  .notch {
    height: env(safe-area-inset-top, 0);
    background: $white;
  }
}
</style>
